import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, BarChart, Bar, PieChart, Pie, Cell,
} from 'recharts';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';
import {
  User, Clock, CheckCircle2, AlertTriangle, TrendingUp, Download, Trophy,
  Target, Calendar, Loader2,
} from 'lucide-react';

const PIE_COLORS = ['#6366f1', '#06b6d4', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#ef4444', '#14b8a6'];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function firstOfMonth(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return d.toISOString().split('T')[0];
}

function lastOfMonth(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset + 1, 0);
  return d.toISOString().split('T')[0];
}

const RANGE_PRESETS = [
  { key: '7d',   label: 'Last 7d',   from: () => daysAgo(6),            to: () => daysAgo(0) },
  { key: '30d',  label: 'Last 30d',  from: () => daysAgo(29),           to: () => daysAgo(0) },
  { key: '90d',  label: 'Last 90d',  from: () => daysAgo(89),           to: () => daysAgo(0) },
  { key: 'mtd',  label: 'This Month', from: () => firstOfMonth(0),      to: () => daysAgo(0) },
  { key: 'last', label: 'Last Month', from: () => firstOfMonth(-1),     to: () => lastOfMonth(-1) },
];

export default function EmployeePerformanceTab({ board }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [range, setRange] = useState('30d');
  const [customFrom, setCustomFrom] = useState(daysAgo(29));
  const [customTo, setCustomTo] = useState(daysAgo(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const contentRef = useRef(null);

  const [dateFrom, dateTo] = (() => {
    if (range === 'custom') return [customFrom, customTo];
    const preset = RANGE_PRESETS.find(p => p.key === range);
    return preset ? [preset.from(), preset.to()] : [daysAgo(29), daysAgo(0)];
  })();

  const loadEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/board-employees`, {
        params: { board }, withCredentials: true,
      });
      setEmployees(data);
      if (data.length > 0 && !selectedEmp) setSelectedEmp(data[0].employee_id);
    } catch (err) { /* admin-only guard */ }
  }, [board, selectedEmp]);

  const loadAnalytics = useCallback(async () => {
    if (!selectedEmp) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/analytics/employee-performance`, {
        params: { board, employee_id: selectedEmp, date_from: dateFrom, date_to: dateTo },
        withCredentials: true,
      });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [board, selectedEmp, dateFrom, dateTo]);

  useEffect(() => { if (isAdmin) loadEmployees(); }, [isAdmin, loadEmployees]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const handleExport = async () => {
    if (!data || !contentRef.current) return;
    setExporting(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);
      const canvas = await html2canvas(contentRef.current, {
        backgroundColor: '#0a0a0f',
        scale: 2,
        useCORS: true,
      });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(canvas, 'PNG', 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(canvas, 'PNG', 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      const fname = `${data.employee.name.replace(/\s+/g, '_')}_${board}_${dateFrom}_${dateTo}.pdf`;
      pdf.save(fname);
      toast.success('PDF exported');
    } catch (err) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  if (!isAdmin) {
    return (
      <div className="glass-card p-8 text-center text-slate-400 text-sm" data-testid="emp-perf-admin-gate">
        <AlertTriangle size={20} className="mx-auto mb-2 text-amber-400" />
        Employee Performance Review is admin-only.
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-4" data-testid="emp-performance-tab">
      {/* ==== CONTROLS (excluded from PDF capture on purpose) ==== */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedEmp} onValueChange={setSelectedEmp}>
          <SelectTrigger className="w-[260px] bg-black/40 border-white/10 text-white h-9 text-xs" data-testid="emp-perf-employee-select">
            <SelectValue placeholder="Select employee" />
          </SelectTrigger>
          <SelectContent className="bg-[#0f0f15] border-white/10 z-[200] max-h-[300px]">
            {employees.map(e => (
              <SelectItem key={e.employee_id} value={e.employee_id}>
                {e.name} <span className="text-slate-500 text-[10px]">· {e.department}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-md p-0.5">
          {RANGE_PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => setRange(p.key)}
              data-testid={`emp-perf-range-${p.key}`}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded ${range === p.key ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setRange('custom')}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold rounded ${range === 'custom' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'}`}
            data-testid="emp-perf-range-custom"
          >
            Custom
          </button>
        </div>

        {range === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="bg-black/40 border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="emp-perf-custom-from" />
            <span className="text-slate-500 text-xs">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="bg-black/40 border border-white/10 text-white rounded px-2 py-1 text-xs" data-testid="emp-perf-custom-to" />
          </div>
        )}

        <div className="flex-1" />

        <Button
          onClick={handleExport}
          disabled={exporting || !data}
          variant="outline"
          className="bg-white/5 border-white/10 text-slate-300 hover:text-white h-9 text-xs"
          data-testid="emp-perf-export-pdf"
        >
          {exporting ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Download size={12} className="mr-1" />}
          Export PDF
        </Button>
      </div>

      {/* ==== CAPTURED CONTENT ==== */}
      <div ref={contentRef} className="space-y-4 p-1 bg-[#0a0a0f]">
        {loading && !data ? (
          <div className="glass-card p-12 text-center text-slate-400">
            <Loader2 size={16} className="inline mr-2 animate-spin" /> Loading…
          </div>
        ) : !data ? (
          <div className="glass-card p-12 text-center text-slate-500 text-sm">
            Pick an employee and time range to see their performance review.
          </div>
        ) : (
          <>
            {/* Header banner */}
            <div className="glass-card p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
                  <User size={20} className="text-white" />
                </div>
                <div>
                  <div className="text-white font-heading font-bold text-lg" data-testid="emp-perf-name">{data.employee.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {data.employee.department} · {data.employee.role || '—'} · {board.toUpperCase()} BOARD
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Review Period</div>
                <div className="text-sm text-white font-mono">{data.period.from} → {data.period.to}</div>
                <div className="text-[10px] text-slate-500">{data.period.days} days</div>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard icon={Target}       label="Total Assigned"     value={data.kpis.total_assigned}     accent="indigo" />
              <KpiCard icon={CheckCircle2} label="Completed"          value={data.kpis.total_completed}    sub={`${data.kpis.completion_rate}% rate`} accent="emerald" />
              <KpiCard icon={Clock}        label="Avg Turnaround"     value={data.kpis.avg_turnaround_days != null ? `${data.kpis.avg_turnaround_days}d` : '—'} accent="cyan" />
              <KpiCard icon={TrendingUp}   label="On-Time %"          value={`${data.kpis.on_time_pct}%`}  accent={data.kpis.on_time_pct >= 80 ? 'emerald' : 'amber'} />
              <KpiCard icon={AlertTriangle} label="Carried Forward"   value={data.kpis.carried_forward}    accent={data.kpis.carried_forward > 5 ? 'rose' : 'slate'} />
              <KpiCard icon={Calendar}     label="Timesheet Hours"    value={`${data.kpis.timesheet_hours}h`} sub={`${data.kpis.timesheet_days_submitted}/${data.period.days} days`} accent="violet" />
              <KpiCard icon={Clock}        label="Avg Hours/Day"      value={data.kpis.avg_hours_per_day != null ? `${data.kpis.avg_hours_per_day}h` : '—'} accent="violet" />
              {board === 'production'
                ? <KpiCard icon={Trophy} label="Production Score" value={data.kpis.production_score ?? '—'} accent="amber" />
                : <KpiCard icon={Trophy} label="Rank" value={data.rank.rank ? `#${data.rank.rank}` : '—'} sub={data.rank.rank ? `of ${data.rank.of_total}` : ''} accent="amber" />}
            </div>

            {/* Trend chart — daily */}
            <div className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Daily Completion Trend</h4>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-4 bg-indigo-500 rounded" />{data.employee.name.split(' ')[0]}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-0.5 w-4 border-t border-dashed border-amber-400" />Team median</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: '#0f0f15', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                  <Line type="monotone" dataKey="employee" stroke="#6366f1" strokeWidth={2} dot={{ r: 2 }} name="Employee" />
                  <Line type="monotone" dataKey="team_median" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" dot={false} name="Team median" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Weekly trend + Carry-forward bars */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="glass-card p-4">
                <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Weekly Output vs Median</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.weekly_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="week_start" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#0f0f15', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                    <Bar dataKey="employee" fill="#6366f1" name="Employee" />
                    <Bar dataKey="team_median" fill="#f59e0b" name="Team median" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="glass-card p-4">
                <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Carried-Forward Timeline (pending at week end)</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.carry_forward_weekly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="week_start" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#0f0f15', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                    <Bar dataKey="carry_forward" fill="#ef4444" name="Pending" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Distribution + Leaderboard */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="glass-card p-4">
                <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Task Type Distribution</h4>
                {data.distribution.length === 0 ? (
                  <div className="py-12 text-center text-slate-600 text-xs">No completed tasks in this period</div>
                ) : (
                  <div className="flex items-center gap-4">
                    <ResponsiveContainer width="55%" height={200}>
                      <PieChart>
                        <Pie data={data.distribution} dataKey="count" nameKey="task_type" cx="50%" cy="50%" outerRadius={80}>
                          {data.distribution.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#0f0f15', border: '1px solid rgba(255,255,255,0.1)', fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-1.5">
                      {data.distribution.map((d, i) => (
                        <div key={d.task_type} className="flex items-center gap-2 text-xs" data-testid={`dist-${d.task_type}`}>
                          <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                          <span className="text-slate-300 flex-1 truncate">{d.task_type}</span>
                          <span className="text-white font-mono font-bold">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="glass-card p-4">
                <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-2">Team Leaderboard</h4>
                <div className="space-y-1.5">
                  {data.rank.leaderboard.length === 0 ? (
                    <div className="py-8 text-center text-slate-600 text-xs">No team data</div>
                  ) : data.rank.leaderboard.map((r, i) => {
                    const me = r.employee_id === data.employee.employee_id;
                    const emp = employees.find(e => e.employee_id === r.employee_id);
                    return (
                      <div key={r.employee_id} className={`flex items-center gap-3 p-2 rounded ${me ? 'bg-indigo-500/10 border border-indigo-500/30' : 'bg-white/[0.02] border border-white/5'}`}>
                        <div className={`h-6 w-6 rounded flex items-center justify-center text-[10px] font-bold ${i === 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-slate-400'}`}>
                          #{i + 1}
                        </div>
                        <div className="text-xs text-white flex-1 truncate">{emp?.name || r.employee_id} {me && <span className="text-[9px] text-indigo-300">· you</span>}</div>
                        <div className="text-xs font-mono text-slate-300">{r.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent = 'slate' }) {
  const accents = {
    indigo:  'bg-indigo-500/10 border-indigo-500/20  text-indigo-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300',
    cyan:    'bg-cyan-500/10 border-cyan-500/20 text-cyan-300',
    amber:   'bg-amber-500/10 border-amber-500/20 text-amber-300',
    rose:    'bg-rose-500/10 border-rose-500/20 text-rose-300',
    violet:  'bg-violet-500/10 border-violet-500/20 text-violet-300',
    slate:   'bg-white/5 border-white/10 text-slate-300',
  };
  const cls = accents[accent] || accents.slate;
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={12} />
        <span className="text-[9px] uppercase tracking-widest font-semibold opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-heading font-black text-white font-mono">{value ?? '—'}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
